import type { ReactNode } from "react"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  ErrorDialog,
  ErrorDialogCsvUploadContent,
} from "@/components/error-dialog"
import { UploadFilesDialog } from "@/components/upload-files-dialog"
import { useActiveOrganization } from "@/contexts"
import { BillPdfParseProgress } from "@/modules/data/components/bill-pdf-parse-progress"
import { BillPdfReviewSection } from "@/modules/data/components/bill-pdf-review-section"
import { useBillPdfUploadSession } from "@/modules/data/services/use-bill-pdf-upload-session"
import type { UploadStepType } from "@/modules/data/utils/upload-analytics"
import {
  trackUploadError,
  trackUploadSuccess,
} from "@/modules/data/utils/upload-analytics"
import { DataFreshnessAlert } from "@/nzds/feedback/data-freshness-alert"
import { useDisclosure } from "@/nzds/utils"
import { useFlags } from "@/services/feature"
import type { HttpError, HttpErrorRecord } from "@/utils/api"
import { getErrorNotificationMessage } from "@/utils/notification-bridge"
import type { UploadFileType } from "@/utils/upload-file-type"
import {
  hasOnlyPdfFiles,
  hasSingleSpreadsheetFile,
} from "@/utils/upload-file-type"
import type { UseMutationResult } from "@tanstack/react-query"
import { kebabCase, lowerFirst } from "lodash-es"

import CloseIcon from "@mui/icons-material/Close"
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined"
import {
  Alert,
  AlertTitle,
  Button,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"

import type { UseUploadMutationVariables } from "../../services/services.types"

interface UploadStepProps {
  downloadTemplateButton: ReactNode
  downloadTemplateDescription: string
  successMessage: string
  type: UploadStepType
  uploadCsvButtonText: string
  uploadDescription: string
  uploadErrorDescription: string
  uploadErrorTitle: string
  uploadQuery: () => UseMutationResult<
    unknown,
    HttpError<HttpErrorRecord>,
    UseUploadMutationVariables
  >
}

// Total record, so adding an UploadStepType is a compile error here rather
// than a step that silently falls back to its raw type string.
const STEP_TITLE_KEY: Record<UploadStepType, string | null> = {
  "additional usage": null,
  "building data": "upload.buildingData.title",
  "interval usage data": "upload.intervalUsageData.title",
  "meter data": "upload.meterData.title",
  "usage data": "upload.usageData.title",
  "user data": "upload.userData.title",
}

const isBillOrIntervalUpload = (type: UploadStepType): boolean =>
  type === "usage data" || type === "interval usage data"

const hasPdfRequestError = (
  isBillPdfStep: boolean,
  requestErrors: boolean[]
): boolean => isBillPdfStep && requestErrors.includes(true)

interface UploadCopyVariant {
  buttonText: string
  description: string
  fileTypeErrorMessage: string
}

interface UploadCopy extends UploadCopyVariant {
  acceptedFileTypes: UploadFileType[]
}

// One decision instead of three parallel flag branches. The variants arrive as
// a named object so two same-typed strings cannot be swapped silently.
const resolveUploadCopy = (
  excelEnabled: boolean,
  isBillPdfStep: boolean,
  variants: {
    csv: UploadCopyVariant
    spreadsheet: UploadCopyVariant
    spreadsheetOrPdf: UploadCopyVariant
  }
): UploadCopy => {
  const acceptedFileTypes: UploadFileType[] = ["csv"]
  if (excelEnabled) {
    acceptedFileTypes.push("xlsx")
  }
  if (isBillPdfStep) {
    acceptedFileTypes.push("pdf")
  }

  if (!excelEnabled) {
    return { ...variants.csv, acceptedFileTypes }
  }

  if (isBillPdfStep) {
    return { ...variants.spreadsheetOrPdf, acceptedFileTypes }
  }

  return { ...variants.spreadsheet, acceptedFileTypes }
}

export const UploadStep: React.FC<UploadStepProps> = ({
  downloadTemplateButton,
  downloadTemplateDescription,
  successMessage,
  type,
  uploadCsvButtonText,
  uploadDescription,
  uploadErrorDescription,
  uploadErrorTitle,
  uploadQuery,
}) => {
  const { t } = useTranslation("common")
  const { t: tData } = useTranslation("data")
  const { activeOrganization } = useActiveOrganization()
  const flags = useFlags()
  const excelEnabled = Boolean(flags.data_manager_excel_upload)
  const isBillPdfStep =
    type === "usage data" && Boolean(flags.data_manager_bill_pdf_upload)
  const stepTitleKey = STEP_TITLE_KEY[type]
  const stepTitle = stepTitleKey ? tData(stepTitleKey) : type
  const spreadsheetButtonText = tData("upload.spreadsheetButton", {
    title: lowerFirst(stepTitle),
  })
  const uploadCopy = resolveUploadCopy(excelEnabled, isBillPdfStep, {
    csv: {
      buttonText: uploadCsvButtonText,
      description: uploadDescription,
      fileTypeErrorMessage: tData("upload.billPdf.invalidFileType"),
    },
    spreadsheet: {
      buttonText: spreadsheetButtonText,
      description: tData("upload.spreadsheetDescription"),
      fileTypeErrorMessage: tData("upload.invalidSpreadsheetFileType"),
    },
    spreadsheetOrPdf: {
      buttonText: tData("upload.usageData.uploadCsvOrPdfButton"),
      description: tData("upload.spreadsheetOrPdfDescription"),
      fileTypeErrorMessage: tData("upload.invalidSpreadsheetOrPdfFileType"),
    },
  })
  const [fileTypeError, setFileTypeError] = React.useState(false)

  const {
    data,
    error,
    isError,
    isPending: isLoading,
    isSuccess,
    mutate,
    reset,
  } = uploadQuery()

  const billPdfSession = useBillPdfUploadSession({
    enabled: isBillPdfStep,
    organizationId: activeOrganization?.id,
  })

  const successMessageFromApi = isBillOrIntervalUpload(type)
    ? ((
        data as
          | {
              data?: {
                data?: {
                  message?: string
                }
              }
            }
          | undefined
      )?.data?.data?.message ?? null)
    : null
  const pdfConfirmMessage =
    billPdfSession.confirmBillPdfUploadSessionData?.message ?? null

  const {
    close: uploadClose,
    isOpen: uploadIsOpen,
    open: uploadOpen,
  } = useDisclosure()

  const {
    close: uploadSuccessClose,
    isOpen: uploadSuccessIsOpen,
    open: uploadSuccessOpen,
  } = useDisclosure()

  const {
    close: uploadFreshnessClose,
    isOpen: uploadFreshnessIsOpen,
    open: uploadFreshnessOpen,
  } = useDisclosure()

  const {
    close: uploadErrorClose,
    isOpen: uploadErrorIsOpen,
    open: uploadErrorOpen,
  } = useDisclosure()

  const isPdfMutationPending =
    billPdfSession.createBillPdfUploadSessionIsPending ||
    billPdfSession.confirmBillPdfUploadSessionIsPending ||
    billPdfSession.cancelBillPdfUploadSessionIsPending ||
    billPdfSession.removeBillPdfUploadSessionRowIsPending ||
    billPdfSession.updateBillPdfUploadSessionRowIsPending

  const resetPdfUploadUi = (): void => {
    reset()
    billPdfSession.resetBillPdfUploadSessionMutations()
    setFileTypeError(false)
  }

  const handleUploadClick = (): void => {
    if (isPdfMutationPending) {
      return
    }

    resetPdfUploadUi()
    uploadSuccessClose()
    uploadFreshnessClose()
    uploadOpen()
  }

  const handleUpload = (files: File[]): void => {
    if (isPdfMutationPending) {
      return
    }

    resetPdfUploadUi()

    if (isBillPdfStep && hasOnlyPdfFiles(files)) {
      uploadClose()
      billPdfSession.createBillPdfUploadSession(files)
      return
    }

    if (hasSingleSpreadsheetFile(files, excelEnabled)) {
      mutate({ file: files[0], organizationId: activeOrganization?.id })
      return
    }

    setFileTypeError(true)
    uploadClose()
    uploadErrorOpen()
  }

  const pdfConfirmSucceeded =
    isBillPdfStep && billPdfSession.confirmBillPdfUploadSessionIsSuccess
  const uploadSucceeded = isSuccess || pdfConfirmSucceeded
  const pdfRequestFailed = hasPdfRequestError(isBillPdfStep, [
    billPdfSession.createBillPdfUploadSessionIsError,
    billPdfSession.confirmBillPdfUploadSessionIsError,
    billPdfSession.cancelBillPdfUploadSessionIsError,
    billPdfSession.billPdfUploadSessionIsError,
  ])
  const uploadFailed = isError || pdfRequestFailed

  React.useEffect(() => {
    if (!uploadSucceeded) {
      return
    }

    uploadClose()
    uploadSuccessOpen()
    trackUploadSuccess(type)
  }, [type, uploadClose, uploadSuccessOpen, uploadSucceeded])

  React.useEffect(() => {
    if (
      !uploadSucceeded ||
      !flags.use_core_schema ||
      !isBillOrIntervalUpload(type)
    ) {
      return
    }

    uploadFreshnessOpen()
  }, [flags.use_core_schema, type, uploadFreshnessOpen, uploadSucceeded])

  React.useEffect(() => {
    if (!uploadFailed) {
      return
    }

    uploadClose()
    uploadErrorOpen()
    trackUploadError(type)
  }, [type, uploadClose, uploadErrorOpen, uploadFailed])

  const pdfSession = billPdfSession.billPdfUploadSessionData
  const pdfError =
    billPdfSession.createBillPdfUploadSessionError ??
    billPdfSession.confirmBillPdfUploadSessionError ??
    billPdfSession.cancelBillPdfUploadSessionError ??
    billPdfSession.billPdfUploadSessionError
  const pendingParseProgress =
    isBillPdfStep && billPdfSession.createBillPdfUploadSessionIsPending ? (
      <BillPdfParseProgress
        files={(
          billPdfSession.createBillPdfUploadSessionVariables?.files ?? []
        ).map((file) => ({
          filename: file.name,
          state: "pending",
        }))}
      />
    ) : null
  const billPdfReviewSection =
    isBillPdfStep && pdfSession != null ? (
      <BillPdfReviewSection
        cancelIsPending={billPdfSession.cancelBillPdfUploadSessionIsPending}
        confirmIsPending={billPdfSession.confirmBillPdfUploadSessionIsPending}
        refreshFiles={billPdfSession.refreshBillPdfUploadSessionFiles}
        removeIsPending={
          billPdfSession.removeBillPdfUploadSessionRowIsPending ||
          billPdfSession.updateBillPdfUploadSessionRowIsPending
        }
        session={pdfSession}
        onCancel={() => {
          billPdfSession.cancelBillPdfUploadSession(pdfSession.id)
        }}
        onConfirm={() => {
          billPdfSession.confirmBillPdfUploadSession(pdfSession.id)
        }}
        onRemove={(rowId) => {
          billPdfSession.removeBillPdfPreviewRow(rowId)
        }}
        onUpdate={billPdfSession.updateBillPdfPreviewRow}
      />
    ) : null

  return (
    <Stack
      sx={{
        gap: 4,
      }}
    >
      <Stack
        sx={{
          gap: 1,
        }}
      >
        <Typography>{downloadTemplateDescription}</Typography>
        <div>{downloadTemplateButton}</div>
      </Stack>
      <Stack
        sx={{
          gap: 1,
        }}
      >
        <Typography>{uploadCopy.description}</Typography>
        <div>
          <Button
            color="primary"
            disabled={isPdfMutationPending}
            onClick={handleUploadClick}
            size="small"
            startIcon={<FileUploadOutlinedIcon />}
            variant="outlined"
          >
            {uploadCopy.buttonText}
          </Button>
        </div>
        <Collapse in={uploadSuccessIsOpen} sx={{ pt: 1, width: "100%" }}>
          <Alert
            action={
              <IconButton
                aria-label={t("button.close")}
                color="inherit"
                size="small"
                onClick={uploadSuccessClose}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity="success"
          >
            <AlertTitle>{t("success")}</AlertTitle>
            {pdfConfirmMessage ?? successMessageFromApi ?? successMessage}
          </Alert>
        </Collapse>
        <DataFreshnessAlert
          open={uploadFreshnessIsOpen}
          onClose={uploadFreshnessClose}
        />
        {pendingParseProgress ?? billPdfReviewSection}
      </Stack>
      <UploadFilesDialog
        acceptedFileTypes={uploadCopy.acceptedFileTypes}
        isSubmitting={isLoading || isPdfMutationPending}
        multiple={isBillPdfStep}
        onClose={uploadClose}
        onUpload={handleUpload}
        open={uploadIsOpen}
        title={uploadCopy.buttonText}
      />
      <ErrorDialog
        confirmText={t("button.reUpload")}
        details={
          fileTypeError ? undefined : (
            <ErrorDialogCsvUploadContent error={pdfError ?? error} />
          )
        }
        id={`upload-${kebabCase(type)}-error`}
        isOpen={uploadErrorIsOpen}
        message={
          fileTypeError
            ? uploadCopy.fileTypeErrorMessage
            : getErrorNotificationMessage(
                pdfError ?? error,
                uploadErrorDescription
              )
        }
        onClose={() => {
          uploadErrorClose()
          resetPdfUploadUi()
        }}
        onConfirm={uploadOpen}
        title={uploadErrorTitle}
      />
    </Stack>
  )
}
